import { Box } from '@mui/material';
import { Button } from '@portals/core/src/components/Button/Button';
import { SplitPaper } from '@portals/core/src/components/SplitPaper/SplitPaper';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import { ArrowRight } from '@portals/icons/src/ArrowRight/ArrowRight';
import { getFixedT } from 'i18next';
import React from 'react';

import { getStyles } from './ModRTeaser.styles';

const t = getFixedT(null, 'core-immobilien');

export interface ModRTeaserParams {
  street?: string;
  streetNumber?: string;
  zip?: string;
  city?: string;
  livingSpace?: string;
  constructionYear?: string;
  heating?: HeatingType;
  housetype?: HouseType;
  floors?: string;
  cellar?: CellarType;
}

export type HouseType = 'free_standing' | 'row_center' | 'row_end' | 'semi_detached' | null;
export type HeatingType =
  | 'natural_gas'
  | 'oil_fuel'
  | 'oven_wood_pellets'
  | 'district_heating_heating_plant'
  | 'electricity'
  | 'oven_wood_chips'
  | null;
export type CellarType = 'totally' | 'partially' | 'unavailable';

const isValidParam = (value: unknown): value is string => value !== undefined && value !== null && value !== '';

export const buildQueryString = (params: ModRTeaserParams): string => {
  const cleanedParams = Object.entries(params)
    .filter(([, value]) => isValidParam(value))
    .reduce((acc, [key, value]) => {
      acc[key] = String(value);
      return acc;
    }, {} as Record<string, string>);

  return new URLSearchParams(cleanedParams).toString();
};

export const ModRTeaser: React.FC<{
  params: ModRTeaserParams;
  onTeaserOpenCallback?: () => void;
  imageSrc?: string;
}> = ({ params, onTeaserOpenCallback, imageSrc = '/assets/Img_ModR_Teaser.png' }) => {
  const styles = getStyles();

  const baseUrl = process.env.MODR_URL;
  const queryString = buildQueryString(params);
  const fullUrl = `${baseUrl}?${queryString}`;

  const handleClick = () => {
    if (typeof onTeaserOpenCallback === 'function') {
      onTeaserOpenCallback();
    }
    window.open(fullUrl, '_blank');
  };

  return (
    <SplitPaper
      button={
        <Button endIcon={<ArrowRight />} onClick={handleClick}>
          {t('detailPage.modRTeaserButtonText')}
        </Button>
      }
      layout="textRightImageFull"
      media={
        <img alt={t('detailPage.modRTeaserImageAlt')} src={imageSrc} style={styles.image as React.CSSProperties} />
      }
      text={
        <Box sx={styles.textContainer}>
          <Typography variant="h2" innerPadding="horizontal" sx={styles.typography}>
            {t('detailPage.modRTeaserHeadline')}
          </Typography>
          <Typography variant="body1" innerPadding="horizontal" sx={styles.typography}>
            {t('detailPage.modRTeaserText')}
          </Typography>
        </Box>
      }
    />
  );
};

ModRTeaser.displayName = 'ModRTeaser';
